// CATÁLOGO DE RUBRICAS — a tela onde a averbadora diz o que cada verba da folha
// significa.
//
// Por que existe: nem o texto nem o cronograma da verba entregam o produto.
// `SAQUE ANTECIPCARD` é cartão benefício e amortiza em 60 parcelas fixas de valor
// constante, igual a um empréstimo; classificado como empréstimo comeria a margem
// de empréstimo em vez da de cartão, e o servidor passaria a poder contratar
// cartão sobre um teto cheio. Produto é declarado, nunca deduzido.
//
// Por isso a descrição CRUA fica em destaque ao lado da escolha: é a única coisa
// que liga o que se está classificando ao que a prefeitura escreveu.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  FormGrid,
  Pagination,
  Pill,
  SelectField,
  TextareaField,
  type Column,
  type PillVariant,
} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { QUERY_CREDORES_OPERAVEIS, listarCredoresOperaveis } from "../../lib/credores-operaveis";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis, rotuloUnidade } from "../../lib/unidades-operaveis";
import { POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { categoriaDaNatureza } from "@atlas/domain";
import { CATEGORIA_LABEL, CATEGORIA_PILL, PRODUTO_LABEL, rotuloTipoServico } from "./motor-rubricas-labels";
import {
  CamposClassificacao,
  classeDaRubrica,
  classificacaoVigente,
  useCamposClassificacao,
  vinculosAtivosPorCodigo,
  type CredorEscolhivel,
} from "./classificacao-campos";
import type { AdminRubrica, AdminRubricasConciliacao } from "@atlas/sdk";

// Uma cópia só do rótulo de produto, sem percentual (ADR-0012: o número é do órgão).
const BUCKET_LABEL = PRODUTO_LABEL;

export function AdminRubricas() {
  const qc = useQueryClient();
  const prefeituras = useQuery({ queryKey: QUERY_UNIDADES_OPERAVEIS, queryFn: listarUnidadesOperaveis });
  const bancos = useQuery({ queryKey: QUERY_CREDORES_OPERAVEIS, queryFn: listarCredoresOperaveis });
  const [unidadeId, setPrefeituraId] = useState<number | null>(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  // Trocar de prefeitura com a posicao antiga cairia num offset inexistente.
  useEffect(() => { setPagina(1); }, [unidadeId]);

  const catalogo = useQuery({
    queryKey: ["admin", "rubricas", unidadeId],
    queryFn: () => atlas.admin.rubricas.list(unidadeId!),
    enabled: unidadeId !== null,
  });

  const pendentes = useMemo(
    () => (catalogo.data?.rubricas ?? []).filter((r) => r.confirmadaEm === null),
    [catalogo.data],
  );
  const confirmadas = useMemo(
    () => (catalogo.data?.rubricas ?? []).filter((r) => r.confirmadaEm !== null),
    [catalogo.data],
  );

  const invalidar = () => qc.invalidateQueries({ queryKey: ["admin", "rubricas", unidadeId] });

  // A classificação de cada linha vem do VÍNCULO ATIVO (#270): é ele que sabe
  // categoria legal, tipo de serviço e desconto obrigatório. A rubrica só
  // conhece a natureza antiga.
  const ativoPorCodigo = useMemo(() => vinculosAtivosPorCodigo(catalogo.data?.vinculos ?? []), [catalogo.data]);
  const classe = (r: AdminRubrica) => classeDaRubrica(r, ativoPorCodigo.get(r.codigoVerba));

  const colunas: Column<AdminRubrica>[] = [
    { key: "codigoOriginal", header: "Verba", mono: true },
    { key: "descricao", header: "Descrição na folha" },
    {
      key: "categoria",
      header: "Categoria",
      render: (r) => {
        const c = classe(r).categoria;
        return <Pill variant={CATEGORIA_PILL[c] as PillVariant}>{CATEGORIA_LABEL[c]}</Pill>;
      },
    },
    {
      key: "tipoServico",
      header: "Tipo de serviço",
      render: (r) => {
        const { categoria, tipoServico } = classe(r);
        if (categoria !== "consignacao_credito" && categoria !== "consignacao_facultativa") return "—";
        // Legado anterior à #270: consignação ainda sem tipo.
        return tipoServico ? rotuloTipoServico(tipoServico) : <Pill variant="pendente">a definir</Pill>;
      },
    },
    { key: "consignataria", header: "Consignatária", render: (r) => r.consignataria ?? "—" },
    {
      key: "tipoMargem",
      header: "Produto",
      render: (r) => {
        const { categoria, produto } = classe(r);
        if (categoria === "consignacao_facultativa") return "pela regra do órgão";
        if (categoria !== "consignacao_credito") return "—";
        return produto ? (
          BUCKET_LABEL[produto]!
        ) : (
          // Crédito sem produto não vira dívida: cai em conciliação.
          <Pill variant="rejeitada">sem produto</Pill>
        );
      },
    },
    {
      key: "ocorrencias",
      header: "Na folha",
      align: "right",
      render: (r) => (r.ocorrencias > 0 ? `${r.ocorrencias} linha(s)` : "—"),
    },
    {
      key: "contratos",
      header: "Contratos",
      align: "right",
      render: (r) => (r.contratos > 0 ? r.contratos.toLocaleString("pt-BR") : "—"),
    },
    {
      key: "confirmadaPor",
      header: "Confirmada por",
      render: (r) => r.confirmadaPor?.replace(/^averbadora:/, "") ?? "—",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Catálogo de rubricas</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 780 }}>
          As verbas de desconto da folha, e o que cada uma significa. O catálogo é por <strong>folha</strong>:
          um município tem mais de uma (administração direta, autarquias, institutos) e a mesma consignatária costuma ter
          código diferente em cada. Verba não confirmada <strong>não gera dívida</strong> — o desconto
          dela cai em conciliação.
        </p>
      </header>

      <div style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 12, padding: 20 }}>
        <FormGrid cols={2}>
          <SelectField
            label="Folha (órgão ou entidade)"
            value={unidadeId ? String(unidadeId) : ""}
            onChange={(e) => setPrefeituraId(e.target.value ? Number(e.target.value) : null)}
            options={[
              { value: "", label: "—" },
              ...(prefeituras.data?.prefeituras.map((p) => ({ value: String(p.id), label: rotuloUnidade(p) })) ?? []),
            ]}
          />
        </FormGrid>
      </div>

      {unidadeId === null ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--text-dim)" }}>
          Selecione a folha para ver o catálogo.
        </div>
      ) : catalogo.isLoading ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--text-dim)" }}>Carregando…</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
            <Stat
              label="Pendentes de confirmação"
              value={String(catalogo.data?.pendentes ?? 0)}
              alerta={(catalogo.data?.pendentes ?? 0) > 0}
              nota="Enquanto houver pendente, o desconto dessa verba cai em conciliação"
            />
            <Stat label="Consignações confirmadas" value={String(catalogo.data?.consignacoesConfirmadas ?? 0)} />
            <Stat
              label="Consignação sem produto"
              value={String(catalogo.data?.semBalde ?? 0)}
              alerta={(catalogo.data?.semBalde ?? 0) > 0}
              nota="Não vira dívida até ter produto definido"
            />
            <Stat
              label="Divergem do relatório"
              value={String(catalogo.data?.divergencias ?? 0)}
              alerta={(catalogo.data?.divergencias ?? 0) > 0}
              nota="O relatório de conciliação declara outro credor"
            />
          </div>

          {(catalogo.data?.divergenciasConvenio.length ?? 0) > 0 && (
            <section>
              <h2 style={{ fontSize: "1.1rem", margin: "0 0 4px" }}>
                Convênios que não batem com o catálogo{" "}
                <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>
                  ({catalogo.data!.divergenciasConvenio.length})
                </span>
              </h2>
              <p style={{ color: "var(--text-muted)", margin: "0 0 12px", fontSize: 13, maxWidth: 780 }}>
                Aviso, não bloqueio: o órgão pode ter trocado a rubrica e não avisado, e travar
                averbação por isso seria pior que o problema. Mas o de <strong>credor diferente</strong> é
                grave — o desconto sai na rubrica errada e o repasse vai para a consignatária errada, e isso só
                apareceria na conciliação do mês seguinte.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {catalogo.data!.divergenciasConvenio.map((d, i) => (
                  <div
                    key={`${d.convenioId}-${d.codigoVerba}-${d.tipo}-${i}`}
                    style={{
                      background: "var(--bg-elev)",
                      // `verba_nao_catalogada` não é erro: o catálogo só conhece
                      // verba que movimentou. Borda neutra, não vermelha.
                      border:
                        d.tipo === "verba_nao_catalogada"
                          ? "1px solid var(--border-strong)"
                          : "1px solid var(--danger, #b3261e)",
                      borderRadius: 8,
                      padding: "10px 12px",
                      fontSize: 13,
                    }}
                  >
                    <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                      <Pill variant={d.tipo === "verba_nao_catalogada" ? "pendente" : "rejeitada"}>
                        {d.tipo.replace(/_/g, " ")}
                      </Pill>
                      <strong>{d.convenioNome}</strong>
                      <span style={{ fontFamily: "var(--font-mono, monospace)" }}>verba {d.codigoVerba}</span>
                      {d.produtoDoConvenio && <span style={{ color: "var(--text-dim)" }}>{d.produtoDoConvenio}</span>}
                    </div>
                    <div style={{ marginTop: 4, color: "var(--text-muted)" }}>{d.mensagem}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {pendentes.length > 0 && (
            <section>
              <h2 style={{ fontSize: "1.1rem", margin: "0 0 4px" }}>
                Pendentes <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>({pendentes.length})</span>
              </h2>
              <p style={{ color: "var(--text-muted)", margin: "0 0 12px", fontSize: 13 }}>
                A descrição vem crua, como a folha a escreveu. Não deduza o produto do texto — se não
                estiver claro, pergunte à folha antes de confirmar.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {pendentes.map((r) => (
                  <CardClassificacao
                    key={r.id}
                    rubrica={r}
                    bancos={bancos.data?.bancos ?? []}
                    onConfirmado={invalidar}
                  />
                ))}
              </div>
            </section>
          )}

          {confirmadas.length > 0 && (
            <section>
              <h2 style={{ fontSize: "1.1rem", margin: "0 0 12px" }}>
                Confirmadas <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>({confirmadas.length})</span>
              </h2>
              <DataTable columns={colunas} rows={recortar(confirmadas, pagina, porPagina).itens} rowKey={(r) => r.id} />
              <Pagination
                total={confirmadas.length}
                pagina={pagina}
                porPagina={porPagina}
                onPagina={setPagina}
                onPorPagina={setPorPagina}
                rotulo="rubricas"
              />
            </section>
          )}

          <PainelConciliacao unidadeId={unidadeId} onAplicado={invalidar} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- classificação

export function CardClassificacao({
  rubrica,
  bancos,
  onConfirmado,
}: {
  rubrica: AdminRubrica;
  bancos: CredorEscolhivel[];
  onConfirmado: () => void;
}) {
  // A categoria NÃO vem pré-preenchida com a sugestão: select cheio com o palpite
  // do holerite parece classificação já feita (issue #176). A sugestão aparece
  // como dica; a consignatária e o produto declarados pelo relatório de
  // conciliação continuam pré-preenchidos — nenhum dos dois confirma nada
  // sozinho. Os campos são os mesmos do Motor de Rubricas (#270).
  const [inicial] = useState(() => classificacaoVigente(rubrica, null));
  const c = useCamposClassificacao(inicial);
  const sugestao =
    rubrica.naturezaSugerida !== "nao_classificada"
      ? CATEGORIA_LABEL[categoriaDaNatureza(rubrica.naturezaSugerida)]
      : null;

  const confirmar = useMutation({
    mutationFn: () => atlas.admin.rubricas.confirmar({ id: rubrica.id, ...c.payload(bancos) }),
    onSuccess: onConfirmado,
    onError: (e) => alert(e instanceof Error ? e.message : "Erro ao confirmar"),
  });

  return (
    <div style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        <span style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 18, fontWeight: 700 }}>
          {rubrica.codigoOriginal}
        </span>
        <span style={{ fontSize: 16 }}>{rubrica.descricao}</span>
        <Pill variant={rubrica.tipo === "D" ? "pendente" : "emdia"}>
          {rubrica.tipo === "D" ? "desconto" : "provento"}
        </Pill>
        {rubrica.servico && <Pill variant="averbado">{rubrica.servico}</Pill>}
      </div>

      <p style={{ color: "var(--text-dim)", fontSize: 12, margin: "0 0 14px" }}>
        {rubrica.ocorrencias > 0 && `${rubrica.ocorrencias} linha(s) no holerite · `}
        {rubrica.primeiraCompetencia && `vista de ${rubrica.primeiraCompetencia} a ${rubrica.ultimaCompetencia} · `}
        {rubrica.contratos > 0 && `${rubrica.contratos} contrato(s) no relatório · `}
        sugestão: {sugestao ?? CATEGORIA_LABEL.nao_classificada}
        {rubrica.fonte === "conciliacao" && " · pré-preenchida pelo relatório de conciliação"}
      </p>

      {rubrica.descricaoMudouEm && (
        <Aviso>
          A folha mudou o texto desta verba. Pode ser troca de consignatária — reconfira o credor.
        </Aviso>
      )}
      {rubrica.divergenciaConciliacao && (
        <Aviso>
          O relatório de conciliação declara <strong>{rubrica.divergenciaConciliacao}</strong> como credor
          desta verba.
        </Aviso>
      )}

      <CamposClassificacao c={c} bancos={bancos} sugestao={sugestao} />

      <div style={{ display: "flex", gap: 12, marginTop: 16, alignItems: "center" }}>
        <Button
          onClick={() => confirmar.mutate()}
          disabled={c.categoria === "" || !c.valida || confirmar.isPending}
        >
          {confirmar.isPending ? "Confirmando…" : "Confirmar classificação"}
        </Button>
        {c.credito && !c.produto && (
          <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
            Sem produto definido esta verba continua sem gerar dívida — mas a classificação fica registrada.
          </span>
        )}
        {c.facultativa && (
          <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
            Entra no movimento pela consignatária, sem contrato. O que ocupa de margem é a regra do órgão.
          </span>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------- relatório de conciliação

function PainelConciliacao({ unidadeId, onAplicado }: { unidadeId: number; onAplicado: () => void }) {
  const [csv, setCsv] = useState("");
  const [previa, setPrevia] = useState<AdminRubricasConciliacao | null>(null);
  const [mapa, setMapa] = useState<Record<string, number | "">>({});

  const analisar = useMutation({
    mutationFn: () => atlas.admin.rubricas.conciliacao({ csv }),
    onSuccess: (r) => {
      setPrevia(r);
      // Pré-liga a folha selecionada quando o relatório traz só uma.
      setMapa(Object.fromEntries(r.folhas.map((f) => [f.folha, r.folhas.length === 1 ? unidadeId : ""])));
    },
    onError: (e) => alert(e instanceof Error ? e.message : "Erro ao ler o relatório"),
  });

  const aplicar = useMutation({
    mutationFn: () =>
      atlas.admin.rubricas.conciliacao({
        csv,
        mapa: Object.fromEntries(
          Object.entries(mapa).filter(([, id]) => id !== "").map(([folha, id]) => [folha, Number(id)]),
        ),
      }),
    onSuccess: (r) => {
      setPrevia(r);
      onAplicado();
    },
    onError: (e) => alert(e instanceof Error ? e.message : "Erro ao aplicar"),
  });

  const prefeituras = useQuery({ queryKey: QUERY_UNIDADES_OPERAVEIS, queryFn: listarUnidadesOperaveis });
  const algumMapeado = Object.values(mapa).some((v) => v !== "");

  return (
    <section style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 12, padding: 20 }}>
      <h2 style={{ fontSize: "1.1rem", margin: "0 0 4px" }}>Preencher o catálogo em lote</h2>
      <p style={{ color: "var(--text-muted)", margin: "0 0 14px", fontSize: 13, maxWidth: 780 }}>
        Serve para <strong>não digitar vinte rubricas à mão</strong>. Se o sistema de consignação que o
        órgão usa hoje exporta um relatório com a consignatária de cada verba, ele <strong>declara</strong>{" "}
        o que a descrição da rubrica não entrega — e daqui o catálogo sai pré-preenchido.
      </p>
      <p style={{ color: "var(--text-muted)", margin: "0 0 14px", fontSize: 13, maxWidth: 780 }}>
        Cole o CSV abaixo. A primeira análise é <strong>seca</strong>: nada é gravado, ela só mostra quais folhas
        o relatório traz. Ligar cada uma ao cadastro da Atlas é o passo humano, e é onde não se pode errar —
        apontar a verba para a consignatária errada faz uma consignatária ver contrato que não é dela. E{" "}
        <strong>pré-preencher não é confirmar</strong>: a rubrica continua pendente até alguém decidir no catálogo.
      </p>

      <TextareaField
        label="CSV do relatório"
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        rows={5}
        placeholder="Folha;Consignatário;Matrícula;…"
      />

      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <Button variant="ghost" onClick={() => analisar.mutate()} disabled={csv.trim().length < 20 || analisar.isPending}>
          {analisar.isPending ? "Analisando…" : "Analisar (sem aplicar)"}
        </Button>
        {previa && (
          <Button onClick={() => aplicar.mutate()} disabled={!algumMapeado || aplicar.isPending}>
            {aplicar.isPending ? "Aplicando…" : "Aplicar às folhas ligadas"}
          </Button>
        )}
      </div>

      {previa && (
        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            {previa.linhas.toLocaleString("pt-BR")} linha(s) lida(s)
            {previa.erros > 0 && ` · ${previa.erros} com erro de layout`}
            {previa.aplicado && ` · ${previa.aplicadas} verba(s) aplicada(s), ${previa.novas} nova(s)`}
          </div>

          {previa.folhas.map((f) => (
            <div key={f.folha} style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: 12, alignItems: "end" }}>
              <div>
                <div style={{ fontWeight: 600 }}>{f.folha}</div>
                <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
                  {f.verbas} verba(s) · {f.contratos.toLocaleString("pt-BR")} contrato(s)
                </div>
              </div>
              <SelectField
                label="Folha na Atlas"
                value={mapa[f.folha] === "" || mapa[f.folha] === undefined ? "" : String(mapa[f.folha])}
                onChange={(e) => setMapa((m) => ({ ...m, [f.folha]: e.target.value ? Number(e.target.value) : "" }))}
                options={[
                  { value: "", label: "— não importar" },
                  ...(prefeituras.data?.prefeituras.map((p) => ({ value: String(p.id), label: rotuloUnidade(p) })) ?? []),
                ]}
              />
            </div>
          ))}

          {previa.conflitos.length > 0 && (
            <Aviso>
              {previa.conflitos.length} verba(s) apontam para mais de um credor e <strong>não</strong> foram
              importadas — catálogo ambíguo não classifica nada:{" "}
              {previa.conflitos
                .map((c) => `${c.codigoVerba} (${c.alternativas.map((a) => a.consignataria).join(" × ")})`)
                .join("; ")}
            </Aviso>
          )}

          {previa.divergencias && previa.divergencias.length > 0 && (
            <Aviso>
              {previa.divergencias.length} verba(s) já confirmadas divergem do relatório e foram mantidas
              como estavam:{" "}
              {previa.divergencias.map((d) => `${d.id} (${d.confirmada} × ${d.relatorio})`).join("; ")}
            </Aviso>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------- bits

function Stat({ label, value, alerta, nota }: { label: string; value: string; alerta?: boolean; nota?: string }) {
  return (
    <div
      style={{
        background: "var(--bg-elev)",
        border: `1px solid ${alerta ? "var(--danger, #b3261e)" : "var(--border-strong)"}`,
        borderRadius: 12,
        padding: "14px 16px",
      }}
    >
      <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 6 }}>{value}</div>
      {nota && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4, lineHeight: 1.35 }}>{nota}</div>}
    </div>
  );
}

function Aviso({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "var(--bg-subtle, rgba(179,38,30,0.06))",
        border: "1px solid var(--danger, #b3261e)",
        borderRadius: 8,
        padding: "10px 12px",
        fontSize: 13,
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}
